/**
 * Retention Policies settings (Spec 201)
 *
 * Retention policies are central, reusable GFS definitions (keep-counts + grandfather-father-son
 * tiers + a soft-delete grace window). Create/edit via a modal (no inline editing); assign per
 * source on the Backups tab. Settings-managed.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info, Pencil, Plus, Archive } from 'lucide-react';
import { Card, CardHeader, CardBody, Button, Badge, DataTable, LoadingState, ErrorState } from '@/components/ui';
import { retentionApi, GfsRetentionPolicy } from '../../api/retention';
import { useAuth } from '../../hooks/useAuth';
import { RetentionPolicyModal } from './RetentionPolicyModal';

const typeVariant: Record<string, 'info' | 'warning' | 'default'> = { lakehouse: 'info', warehouse: 'warning' };
const n = (v: number | null): string => (v == null ? '–' : String(v));

/** Compact human summary of a GFS policy's rules. */
const summarize = (p: GfsRetentionPolicy): string => {
  const parts: string[] = [];
  if (p.keepFull != null) parts.push(`keep ${p.keepFull} full`);
  if (p.keepIncremental != null) parts.push(`${p.keepIncremental} incr`);
  const gfs = [
    p.gfsDaily != null ? `${p.gfsDaily}d` : null,
    p.gfsWeekly != null ? `${p.gfsWeekly}w` : null,
    p.gfsMonthly != null ? `${p.gfsMonthly}mo` : null,
    p.gfsYearly != null ? `${p.gfsYearly}y` : null,
  ].filter(Boolean);
  if (gfs.length) parts.push(`GFS ${gfs.join('/')}`);
  return parts.join(' · ') || 'no rules set';
};

export const RetentionPoliciesSettings: React.FC = () => {
  const { hasRole } = useAuth();
  const canManage = hasRole(['admin', 'operator']);
  const policies = useQuery({ queryKey: ['backup', 'retention-policies'], queryFn: retentionApi.listPolicies });
  const [modal, setModal] = useState<{ policy: GfsRetentionPolicy | null } | null>(null);

  if (policies.isLoading) return <LoadingState rows={4} />;
  if (policies.isError) return <ErrorState onRetry={() => policies.refetch()} />;

  return (
    <Card>
      <CardHeader
        title="Retention policies"
        subtitle="How long backup copies are kept. Create reusable policies here; assign one per source on the Backups tab."
        action={
          canManage ? (
            <Button variant="primary" size="sm" onClick={() => setModal({ policy: null })}>
              <Plus className="h-4 w-4" /> New policy
            </Button>
          ) : undefined
        }
      />
      <CardBody>
        <div className="mb-4 flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
          <p>
            <strong>Keep counts</strong> are a floor — the most recent N full / N incremental backups are always retained,
            giving a dense short-term recovery window. <strong>GFS</strong> (grandfather-father-son) then thins older backups
            to one representative per day / week / month / year, so a year of dailies collapses to ~20 recovery points instead
            of 365 — cheap, compliance-grade history. The two work together: recent stays dense, old goes sparse, and a backup
            is pruned only when it falls outside the keep-counts <em>and</em> isn&apos;t a GFS representative. A pruned backup
            isn&apos;t destroyed right away — the <strong>grace window</strong> keeps it restorable from the recycle bin for a
            few days before it is permanently purged (a <strong>legal hold</strong> exempts a backup from both). A{' '}
            <strong>default</strong> policy covers any source of its type with no policy assigned — set sensible defaults once,
            override only where needed.
          </p>
        </div>
        <DataTable<GfsRetentionPolicy>
          minWidth="44rem"
          columns={[
            {
              key: 'name',
              header: 'Policy',
              width: '22rem',
              sortable: true,
              render: (p) => (
                <span className="flex flex-wrap items-center gap-2">
                  <Archive className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
                  <span className="font-medium text-[var(--color-text-primary)]">{p.name}</span>
                  {p.appliesToEntityType && <Badge variant={typeVariant[p.appliesToEntityType] ?? 'default'}>{p.appliesToEntityType}</Badge>}
                  {p.isDefault && <Badge variant="success">default</Badge>}
                </span>
              ),
            },
            { key: 'rules', header: 'Rules', wrap: true, sortable: false, render: (p) => <span className="text-[var(--color-text-secondary)]">{summarize(p)}</span> },
            { key: 'graceDays', header: 'Grace', width: '7rem', sortable: true, render: (p) => <span className="text-[var(--color-text-secondary)]">{n(p.graceDays)} d</span> },
            {
              key: 'actions',
              header: '',
              width: '7rem',
              align: 'right',
              sortable: false,
              render: (p) =>
                canManage ? (
                  <Button variant="outline" size="sm" onClick={() => setModal({ policy: p })}>
                    <Pencil className="h-4 w-4" /> Edit
                  </Button>
                ) : null,
            },
          ]}
          rows={policies.data ?? []}
          rowKey={(p) => p.id}
        />
      </CardBody>

      <RetentionPolicyModal open={!!modal} policy={modal?.policy ?? null} onClose={() => setModal(null)} />
    </Card>
  );
};

export default RetentionPoliciesSettings;
